import { useDevModeStore } from '@/stores/useDevModeStore'
import { useIsMobile } from '@/hooks/use-mobile'
import { useRouteContext } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import {
  MobileDrawer,
  MobileDrawerTrigger,
  MobileDrawerContent,
  MobileDrawerBody,
  MobileDrawerFooter,
  MobileDrawerClose,
  MobileDrawerTitle,
} from './ui/mobile-drawer'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { Button } from './ui/button'
import {
  ClipboardCopyIcon,
  CogIcon,
  LogOutIcon,
  Building2,
  Check,
} from 'lucide-react'
import { Link, useRouter } from '@tanstack/react-router'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from './ui/dropdown-menu'
import { useSignOut } from '@/hooks/useSignOut'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'

export function UserNavMenu() {
  const router = useRouter()
  const { authState, availableTenants } = useRouteContext({
    from: '/_authenticated',
  })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()

  const isMobile = useIsMobile()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { devMode } = useDevModeStore()
  const signOut = useSignOut()
  const setSelectedTenantId = useSelectedTenantStore(
    (state) => state.setSelectedTenantId,
  )
  const handleCopyAuthToken = useCallback(() => {
    navigator.clipboard.writeText(idToken ?? '')
  }, [idToken])

  const handleDrawerOpenChange = (open: boolean) => {
    setIsDrawerOpen(open)
  }

  const handleTenantSwitch = useCallback(
    (tenantId: string) => {
      if (tenantId === TENANT_ID) return // Already selected

      // Update localStorage with new tenant selelection
      setSelectedTenantId(tenantId)

      // Invalidate the router to trigger re-authentication with new tenant
      router.invalidate({
        filter: (route) => route.id.includes('_authenticated'),
      })

      // Navigate to home/documents to refresh the app with new tenant
      router.navigate({ to: '/documents' })
    },
    [TENANT_ID, router, setSelectedTenantId],
  )

  const handleSwitchOrganization = useCallback(() => {
    router.navigate({ to: '/select-tenant' })
  }, [router])

  // Check if user has multiple tenants to show the switching option
  const hasMultipleTenants = availableTenants && availableTenants.length > 1

  if (isMobile) {
    return (
      <MobileDrawer open={isDrawerOpen} onOpenChange={handleDrawerOpenChange}>
        <MobileDrawerTrigger asChild>
          <Avatar
            className="focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
            role="button"
            tabIndex={0}
          >
            <AvatarImage src={authState?.photoURL ?? ''} />
            <AvatarFallback>
              {authState?.displayName?.split(' ').map((name) => name[0]) ?? ''}
            </AvatarFallback>
          </Avatar>
        </MobileDrawerTrigger>
        <MobileDrawerContent>
          <VisuallyHidden>
            <MobileDrawerTitle>User Menu</MobileDrawerTitle>
          </VisuallyHidden>
          <MobileDrawerBody>
            {devMode && (
              <>
                <div className="p-2">
                  <p className="w-full max-w-40 wrap-anywhere">
                    {authState?.uid}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={handleCopyAuthToken}
                >
                  <ClipboardCopyIcon className="mr-2 size-4" />
                  Copy Auth Token
                </Button>
              </>
            )}
            {hasMultipleTenants && (
              <>
                <div className="text-muted-foreground px-2 py-1.5 text-sm font-medium">
                  Switch Organization
                </div>
                {availableTenants?.map((tenant) => (
                  <Button
                    key={tenant.id}
                    variant="ghost"
                    className="w-full justify-start text-left"
                    onClick={() => handleTenantSwitch(tenant.id)}
                  >
                    <div className="flex w-full items-center gap-2">
                      <Building2 className="size-4" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">
                          {tenant.name}
                        </div>
                        <div className="text-muted-foreground truncate text-xs">
                          {tenant.description}
                        </div>
                      </div>
                      {tenant.id === TENANT_ID && (
                        <Check className="size-4 text-green-600" />
                      )}
                    </div>
                  </Button>
                ))}
                <div className="bg-border mx-2 my-2 h-px" />
              </>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                router.navigate({
                  to: '/user/$userId',
                  params: { userId: authState?.uid! },
                })
              }}
            >
              <CogIcon className="size-4" />
              Settings
            </Button>
            <Button
              variant="ghost"
              className="text-destructive w-full justify-start"
              onClick={() => void signOut()}
            >
              <LogOutIcon className="mr-2 size-4" />
              Log Out
            </Button>
          </MobileDrawerBody>
          <MobileDrawerFooter>
            <MobileDrawerClose asChild>
              <Button variant="outline" className="h-11 w-full rounded-lg">
                Close
              </Button>
            </MobileDrawerClose>
          </MobileDrawerFooter>
        </MobileDrawerContent>
      </MobileDrawer>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar
          className="focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          role="button"
          tabIndex={0}
        >
          <AvatarImage src={authState?.photoURL ?? ''} />
          <AvatarFallback>
            {authState?.displayName?.split(' ').map((name) => name[0]) ?? ''}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-w-80 min-w-40">
        {devMode && (
          <>
            <DropdownMenuItem>
              <p className="w-full max-w-40 wrap-anywhere">{authState?.uid}</p>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyAuthToken} role="button">
              <ClipboardCopyIcon />
              Copy Auth Token
            </DropdownMenuItem>
          </>
        )}
        {hasMultipleTenants && (
          <DropdownMenuItem onClick={handleSwitchOrganization}>
            <Building2 />
            Switch Organization
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link
            to="/user/$userId"
            params={{ userId: authState?.uid! }}
            className="flex flex-row items-center gap-2 text-nowrap"
          >
            <CogIcon />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void signOut()} variant="destructive">
          <LogOutIcon />
          Log Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
